/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Jaroslav Přibyl (pribyl@cadwork.cz)
 *
 */
import AbstractModule from "../abstract_module";
import ILightingModule from "./scene_lighting_interface";
import {Debug, math, RenderDocument} from "js_renderer";
import LightingSettings from "./lighting_settings";
import ShadowLightingSettings from "./shadow_lighting_settings";
import VRLightingSettings from "./vr_lighting_settings";
import Fog from "./fog";
import SSAO from "./ssao";
import {vec2, vec3, vec4} from "gl-matrix";
import {EBackgroundType} from "../background/scene_background_enums";
import Scene from "../../scene";
import {ELightingDateTimeType, EShadowType, ESunDirectionType, ICBType} from "./scene_lighting_enums";
import Settings from "../settings";
import {TSkyParams} from "./lighting_settings_types";

interface ISsao {
    enabled?: boolean;
    radius?: number;
    bias?: number;
}

interface IFog {
    enabled?: boolean;
    density?: number;
    distance?: number;
    // this._defaultSettings.fog.color = [0.0, 0.0, 1.0];
    // this._defaultSettings.fog.type = auto|custom color; // todo?
}

interface ISky {
    luminance?: number;
    turbidity?: number;
    mieCoefficient?: number;
    rayleigh?: number;
    mieDirectionalG?: number;
}

interface ILightingShadowsSettings {
    type?: EShadowType;
    shadowIntensity?: number;
    showSV?: boolean;
    showSM?: boolean;
}

interface ILightingSunlightSettings {
    sunLightEnabled?: boolean;
    handling?: ESunDirectionType;
    sunlightIntensity?: number;
    sunOrientationAngle?: number;
    sunElevationAngle?: number;
    dateTime?: Date;
    dateTimeType?: ELightingDateTimeType;
}

interface ILightingSettings {
    emulateShadowsModule?: boolean;
    ambientIntensity?: number;
    headLightEnabled?: boolean;
    headlightIntensity?: number;
    minimalDiffuseLight?: number;
    ambientEqualsDiffuse?: boolean;
    sunlight?: ILightingSunlightSettings;
    shadows?: ILightingShadowsSettings;
    sky?: ISky;
    fog?: IFog;
    ssao?: ISsao;
}

export type ICBType = () => void;

export default
class LightingModule extends AbstractModule implements ILightingModule {

    protected static _typeName: string = "lighting";

    protected _defaultSettings: ILightingSettings;
    protected _settings: ILightingSettings;

    // TB todo: this should be handled by a general module settings
    private generalLighting: LightingSettings;
    private shadowLighting: ShadowLightingSettings;
    private vrLighting: VRLightingSettings;
    private currentLighting: LightingSettings;
    private _lightingInVS: boolean;
    private _fog: Fog;
    private _ssao: SSAO;
    private _shadowType: EShadowType.NONE;
    private _enableShadowsCB: ICBType;
    private _svEnabledCB: ICBType;
    private _smEnabledCB: ICBType;
    private _disableSvCB: ICBType;
    private _disableSmCB: ICBType;
    private _shadowsDisabledCB: ICBType;

    private readonly _TO_RADIANS: number = 3.14159265358979323846 / 180.0;
    private readonly _TO_DEGREES: number = 180.0 / 3.14159265358979323846;
    private readonly _NORTH_Y_TRANSFORM = - 3.14159265358979323846 * 0.5;  // PI/2 [radians]


/*
    this._defaultSettings.shadowType = 0; // 0...no, 1...SV, 2...SM
    this._defaultSettings.shadowIntensity = 0.7;
    this._defaultSettings.ambientIntensity = 0.20;
    this._defaultSettings.headlightIntensity = 0.80;
    this._defaultSettings.sunlightIntensity = 0;
    this._defaultSettings.sunOrientationAngle = 0;
    this._defaultSettings.sunElevationAngle = 0;
    this._defaultSettings.sunDirectionType = 0; // 0...manual, 1...date and time
    this._defaultSettings.emulateShadowsModule = false;
    this._defaultSettings.showSV = true;
    this._defaultSettings.showSM = true;

 */

    constructor(aRenderDocument: RenderDocument) {
        super(aRenderDocument);

        this._type = LightingModule._typeName;

        this._defaultSettings = {
            emulateShadowsModule: false,
            ambientIntensity: 0.20,
            headlightIntensity: 0.80,
            shadows: {
                type: EShadowType.NONE,
                shadowIntensity: 0.70,
                showSV: true,
                showSM: true
            },
            sunlight: {
                sunLightEnabled: true,
                handling: ESunDirectionType.MANUAL,
                sunlightIntensity: 0,
                sunOrientationAngle: 0,
                sunElevationAngle: 0,
                dateTime: new Date(),
                dateTimeType: ELightingDateTimeType.MODEL
            },
            fog: {
                enabled: false,
                density: 6.0,
                distance: 800000.0
            },
            ssao: {
                enabled: false,
                radius: 0.14,
                bias: 0.006
            },
            sky: {
                turbidity: 4.5, // 10.0, (original default values)
                rayleigh: 1.683, // 2.0,
                mieCoefficient: 0.01, // 0.005,
                mieDirectionalG: 0.605, // 0.8,
                luminance: 1.01 // 1.0
            }
        };

        this._settings = Settings.clone(this._defaultSettings);

        //this._dependencies = ['background']; // ['logo', 'hud', 'lighting'] // hud for compass

        // define default lighting for normal, shadow and vr modes
        this.generalLighting = new LightingSettings(aRenderDocument);
        this.shadowLighting = new ShadowLightingSettings(aRenderDocument);
        this.vrLighting = new VRLightingSettings(aRenderDocument);

        this.currentLighting = this.generalLighting;

        this._lightingInVS = false;

        // the fog and ssao is handled outside the lighting settings as we do not expec that they wil change between
        // switching of general and shadow lighting
        this._fog = new Fog(aRenderDocument);
        this._ssao = new SSAO(aRenderDocument);

        // shadow callbacks
        this._enableShadowsCB = null;
        this._svEnabledCB = null;
        this._smEnabledCB = null;
        this._disableSvCB = null;
        this._disableSmCB = null;
        this._shadowsDisabledCB = null;
    }

    public static getClassType(): string {
        return LightingModule._typeName;
    }

    public setEnableShadowsCB(aCaller: any, aCB: ICBType) {
        this._enableShadowsCB = aCB.bind(aCaller);
    }

    public setShadowVolumesEnabledCB(aCaller: any, aCB: ICBType) {
        this._svEnabledCB = aCB.bind(aCaller);
    }

    public setShadowMapEnabledCB(aCaller: any, aCB: ICBType) {
        this._smEnabledCB = aCB.bind(aCaller);
    }

    public setDisableShadowVolumesCB(aCaller: any, aCB: ICBType) {
        this._disableSvCB = aCB.bind(aCaller);
    }

    public setDisableShadowMapCB(aCaller: any, aCB: ICBType) {
        this._disableSmCB = aCB.bind(aCaller);
    }

    public setShadowsDisabledCB(aCaller: any, aCB: ICBType) {
        this._shadowsDisabledCB = aCB.bind(aCaller);
    }

    public setDefaultLighting() {
        this.generalLighting = new LightingSettings(this.renderDocument);
        this.currentLighting = this.generalLighting;

        this._updateLightsFromSetting(this.currentLighting);
    }

    public pushCurrentLighting() {
        this.currentLighting.push();
    }

    public popCurrentLighting() {
        this.currentLighting.pop();
    }

    public enableSunLight(aValue: boolean): boolean {
        this.currentLighting.setSunLightEnabled(aValue);
        this.sceneAPI.getShadowsModule().invalidateShadowMapAndLight();

        let viewModeModule = this.sceneAPI.viewModeModule;
        if (!viewModeModule) {
            return false;
        }

        let views = viewModeModule.getViews();
        for (let i = 0; i < views.length; i++) {
            let view = views[i];
            let sunLight = view.getSunLight();
            if (sunLight) {
                sunLight.enable(aValue);
            }
        }

        return true;
    }

    public enableHeadLight(aValue: boolean): boolean {
        this.currentLighting.setHeadLightEnabled(aValue);
        this.sceneAPI.getShadowsModule().invalidateShadowMapAndLight();

        let viewModeModule = this.sceneAPI.viewModeModule;
        if (!viewModeModule) {
            return false;
        }

        let views = viewModeModule.getViews();
        for (let i = 0; i < views.length; i++) {
            let view = views[i];
            let headLight = view.getHeadLight();
            if (headLight) {
                headLight.enable(aValue);
            }
        }

        return true;
    }

    public getHeadLightEnabled(): boolean {
        return this.currentLighting.getHeadLightEnabled();
    }

    public getSunLightEnabled(): boolean {
        return this.currentLighting.getSunLightEnabled();
    }

    public setShadowIntensity(aValue: number) {
        this.currentLighting.setShadowIntensity(aValue);
        this.sceneAPI.viewModeModule.scheduleRedraw();
    }

    public getShadowIntensity(): number {
        return this.currentLighting.getShadowIntensity();
    }

    public getShadowType(): EShadowType {
        return this._settings.shadows.type;
    }

    public setHeadLightIntensity(aValue: number) {
        this.currentLighting.setHeadLightIntensity(aValue);

        let viewModeModule = this.sceneAPI.viewModeModule;
        if (!viewModeModule) {
            return false;
        }

        let views = viewModeModule.getViews();
        for (let i = 0; i < views.length; i++) {
            let view = views[i];
            let headLight = view.getHeadLight();
            if (headLight) {
                headLight.setIntenzity(aValue);
            }
        }
        this.sceneAPI.viewModeModule.scheduleRedraw();
    }

    public getHeadLightIntensity(): number {
        return this.currentLighting.getHeadLightIntensity();
    }

// Environment settings
    public setAmbientLightIntensity(aValue: number) {
        this.currentLighting.setAmbientLightIntensity(aValue);
        this.sceneAPI.viewModeModule.scheduleRedraw();
    }

    public getAmbientLightIntensity(): number {
        return this.currentLighting.getAmbientLightIntensity();
    }

    public setMinimalDiffuseLight(aValue: number) {
        this.currentLighting.setMinimalDiffuseLight(aValue);
    }

    public getMinimalDiffuseLight(): number {
        return this.currentLighting.getMinimalDiffuseLight();
    }

// end environment settings

    public setSunLightIntensity(aValue: number) {
        this.currentLighting.setSunLightIntensity(aValue);

        let viewModeModule = this.sceneAPI.viewModeModule;
        if (!viewModeModule) {
            return false;
        }

        let views = viewModeModule.getViews();
        for (let i = 0; i < views.length; i++) {
            let view = views[i];
            let sunLight = view.getSunLight();
            if (sunLight) {
                sunLight.setIntenzity(aValue);
            }
        }
        this.sceneAPI.viewModeModule.scheduleRedraw();
    }

    public getSunLightIntensity(): number {
        return this.currentLighting.getSunLightIntensity();
    }

    public setAmbientEqualsDiffuse(aValue: boolean) {
        this.currentLighting.setAmbientEqualsDiffuse(aValue);
    }

    public getAmbientEqualsDiffuse(): boolean {
        return this.currentLighting.getAmbientEqualsDiffuse();
    }

    public setSunLightOrientation(aAngleRad: number) {
        // then we take the main models's zeropoint rotation and add it to the yRotation
        let mainModel = this.sceneAPI.getDocument().getMainModel();
        let zeropoint = mainModel.getZeropointSettingsRef().zeropoint;
        if (zeropoint) {
            aAngleRad = aAngleRad + zeropoint.getRotation();
        }

        this.currentLighting.setSunLightOrientation(aAngleRad);
        this._updateSunPosition(this.getSunDirection());
    }

    public getSunOrientationAngle(): number {
        return this.currentLighting.getSunLightOrientation();
    }

    public setSunLightElevation(aAngleRad: number) {
        this.currentLighting.setSunLightElevation(aAngleRad);
        this._updateSunPosition(this.getSunDirection());

        if (this.sceneAPI.getBackgroundModule().getBackgroundType() === EBackgroundType.DAYNIGHT) {
            let fogColor = this._fog.getColorFromGradient(aAngleRad);
            this._fog.setColor(fogColor);
            this.updateDayNightAmbientAndSunlight(aAngleRad);
        }
    }

    public getSunElevationAngle(): number {
        return this.currentLighting.getSunLightElevation();
    }

    public getSunDirection(): vec3 {
        let x = Math.cos(this.getSunOrientationAngle());
        let z = Math.sin(this.getSunOrientationAngle());

        let c = Math.sqrt((x * x) + (z * z));
        let elevationAngle = this.getSunElevationAngle();

        let arcTan;
        if (Math.abs(Math.PI / 2.0 - elevationAngle) < 0.001) { // 90
            arcTan = Math.tan(this.getSunElevationAngle() * 0.999);
        } else if (Math.abs(Math.PI * 1.5 - elevationAngle) < 0.001) { // 270 (aka -90)
            arcTan = -Math.tan(this.getSunElevationAngle() * 0.999);
        } else {
            arcTan = Math.tan(this.getSunElevationAngle());
        }
        let y = c * arcTan;

        let sunPosition = vec3.fromValues(x, y, z);
        vec3.normalize(sunPosition, sunPosition);

        return sunPosition;
    }

    public getDateTime(): any {
        return this._settings.sunlight.dateTime;
    }

    public recalculate() {
        // let dateTime = null;
        // if (this._settings.sunlight.dateTimeType === ELightingDateTimeType.LOCAL) {
        //     dateTime = new Date();
        // }
        // else if (this._settings.sunlight.dateTimeType === ELightingDateTimeType.MODEL) {
        //     dateTime = this._settings.sunlight.dateTime;
        // }

        this._recalculate(this._settings.sunlight.dateTime);
    }

    public getSSAO(): SSAO {
        return this._ssao;
    }

    public getFog(): Fog {
        return this._fog;
    }

    public getEmulateShadowsModule(): boolean
    {
        return this._settings.emulateShadowsModule;
    }

//     turbidity            : 10,
//     rayleigh             : 2,
//     mieCoefficient       : 0.005,
//     mieDirectionalG      : 0.8,
//     luminance            : 2
    public getSkyParams(): TSkyParams {
        return this.currentLighting.getSkyParams();
    }

    public enableLightingForVR(aValue: boolean) {
        if (aValue) {
            this.currentLighting = this.vrLighting;
        } else {
            this.currentLighting = this.generalLighting;
        }

        this._updateLightsFromSetting(this.currentLighting);
    }

    public enableShadows(aValue: boolean) {
        if (aValue) {
            this.currentLighting = this.shadowLighting;
        } else {
            this.currentLighting = this.generalLighting;
        }

        this._updateLightsFromSetting(this.currentLighting);
    }

    public enableFastLighting(aValue: boolean) {
        this._lightingInVS = aValue;

        this.renderDocument.getRenderer().scheduleLightingSettingsChanged();
    }

    public getFastLightingEnabled(): boolean {
        return this._lightingInVS;
    }

    public viewChanged() {
        //
    }

    public viewResized(aContentSize: vec2) {
        //
    }

    public getSunLightElevation(): number {
        return 0;
    }

    // todo apply settings test it for false data
    public setFromModelDescription(aJsonObject: any) {
        let settings: ILightingSettings = {};

        settings.shadows = {};
        settings.sunlight = {};

        // backward compatibility
        if (aJsonObject.hasOwnProperty("ModelConfiguration")) {
            let modelConfig: any = aJsonObject.ModelConfiguration;

            if (modelConfig.hasOwnProperty('showShadowsAtStart')) {
                settings.shadows.type = EShadowType.SM;
            }
        }

        if (aJsonObject.hasOwnProperty("ModelLightingSettings")) {
            let lightingConfig: any = aJsonObject.ModelLightingSettings;

            if (lightingConfig.hasOwnProperty('sunLightMode')) {
                settings.sunlight.handling = lightingConfig.sunLightMode;
                if (lightingConfig.sunLightMode === 2) {
                    settings.sunlight.handling = ESunDirectionType.DATETIME;
                }
            }

            if (lightingConfig.hasOwnProperty('ambientLightIntensity')) {
                settings.ambientIntensity = lightingConfig.ambientLightIntensity;
            }

            if (lightingConfig.hasOwnProperty('headLightIntensity')) {
                settings.headlightIntensity = lightingConfig.headLightIntensity;
                settings.headLightEnabled = settings.headlightIntensity > 0;
            }

            if (lightingConfig.hasOwnProperty('ambientEqualsDiffuse')) {
                settings.ambientEqualsDiffuse = lightingConfig.ambientEqualsDiffuse;
            }

            if (lightingConfig.hasOwnProperty('sunLightIntensity')) {
                settings.sunlight.sunlightIntensity = lightingConfig.sunLightIntensity;
                settings.sunlight.sunLightEnabled = lightingConfig.sunLightIntensity > 0;
            }

            if (lightingConfig.hasOwnProperty('sunLightOrientation')) {
                settings.sunlight.sunOrientationAngle = lightingConfig.sunLightOrientation;
            }

            if (lightingConfig.hasOwnProperty('sunLightElevation')) {
                settings.sunlight.sunElevationAngle = lightingConfig.sunLightElevation;
            }

            if (lightingConfig.hasOwnProperty('shadowIntensity')) {
                settings.shadows.shadowIntensity = lightingConfig.shadowIntensity;
            }

            if (lightingConfig.hasOwnProperty('sunLightElevationDateTime')) {
                // todo
                if (lightingConfig.sunLightElevationDateTime !== "") {
                    settings.sunlight.dateTime = new Date(lightingConfig.sunLightElevationDateTime);
                }

            }

            if (lightingConfig.hasOwnProperty('earthCoordinateLatitude')) {
                // todo
            }
            if (lightingConfig.hasOwnProperty('earthCoordinateLongitude')) {
                // todo
            }

            settings.emulateShadowsModule = true;
            if (lightingConfig.hasOwnProperty('advancedLighting')) {
                settings.emulateShadowsModule = false;
            }

        }
        // end backward compatibility

        if (aJsonObject.hasOwnProperty(this.getType())) {
            settings = aJsonObject[this.getType()];
        }

        this.set(settings);
    }

    /**
     * Update ambient and sunlight intensity according to sun elevation angle.
     *
     * @param aAngleRad Sun elevation angle in radians.
     */
    protected updateDayNightAmbientAndSunlight(aAngleRad: number) {
        let maxSun = 1;
        let maxAmb = 0.3;
        let minAmb = 0.1;
        let minSun = 0.2;

        let sinusElev = Math.sin(aAngleRad);
        if (sinusElev < 0) {
            sinusElev = 0;
        }
        let ambient = (maxAmb - minAmb) * sinusElev + minAmb;
        let sunlight = (maxSun - minSun) * sinusElev + minSun;
        this.set({
            ambientIntensity: ambient,
            sunlight: {
                sunlightIntensity: sunlight
            }
        }, true);

        // console.log(ambient + " s " + sunlight);
    }

    protected applySettings(aChangedSettings: any, aPreviousSettings: any) {
        if (aChangedSettings) {

            if (aChangedSettings.hasOwnProperty('sunlight')) {
                let sunlight = aChangedSettings.sunlight;

                if (sunlight.hasOwnProperty('handling')) {
                    if (sunlight.handling === ESunDirectionType.DATETIME) {
                        this.recalculate();
                    }
                }

                if (sunlight.hasOwnProperty('sunlightIntensity')) {
                    this.setSunLightIntensity(sunlight.sunlightIntensity);
                    this.enableSunLight(sunlight.sunlightIntensity > 0);
                }
                if (sunlight.hasOwnProperty('sunlightEnabled')) {
                    this.enableSunLight(sunlight.sunlightEnabled);
                }

                if (sunlight.hasOwnProperty('sunOrientationAngle')) {
                    this.setSunLightOrientation(sunlight.sunOrientationAngle);
                }
                if (sunlight.hasOwnProperty('sunElevationAngle')) {
                    this.setSunLightElevation(sunlight.sunElevationAngle);
                }
                if (sunlight.hasOwnProperty('dateTimeType')) {
                    // this.setSunLightIntensity(sunlight.sunlightIntensity);
                    if (sunlight.dateTimeType === ELightingDateTimeType.LOCAL) {
                        this._settings.sunlight.dateTime = new Date();
                        sunlight.dateTime = this._settings.sunlight.dateTime;

                    }
                    this.recalculate();
                }
                if (sunlight.hasOwnProperty('dateTime')) {
                    this.recalculate();
                }
            }

            if (aChangedSettings.hasOwnProperty('shadows')) {
                let shadows = aChangedSettings.shadows;
                if (shadows.hasOwnProperty('shadowIntensity')) {
                    this.setShadowIntensity(shadows.shadowIntensity);
                }

                // temp solution: done in app
                if (shadows.hasOwnProperty('type')) {
                    let self = this;
                    if (this._shadowType !== EShadowType.NONE) {
                        this._disableShadows(this._shadowType);
                    }

                    if (shadows.type === EShadowType.SV) {
                        this.sceneAPI.shadowsModule.enableShadowVolumes(function() {
                            if (self._svEnabledCB) {
                                self._svEnabledCB();
                            }
                        });
                    }
                    else if (shadows.type === EShadowType.SM) {
                        this.sceneAPI.shadowsModule.setShadowMapEnabled(true);
                        if (self._smEnabledCB) {
                            self._smEnabledCB();
                        }
                    }
                    this._shadowType = shadows.type;
                }
            }

            if (aChangedSettings.hasOwnProperty('ambientIntensity')) {
                this.setAmbientLightIntensity(aChangedSettings.ambientIntensity);
            }

            if (aChangedSettings.hasOwnProperty('headlightIntensity')) {
                this.setHeadLightIntensity(aChangedSettings.headlightIntensity);
                this.enableHeadLight(aChangedSettings.headlightIntensity > 0);
            }
            if (aChangedSettings.hasOwnProperty('headlightEnabled')) {
                this.enableHeadLight(aChangedSettings.headlightEnabled);
            }

            if (aChangedSettings.hasOwnProperty('fog')) {
                let fog = aChangedSettings.fog;
                if (fog.hasOwnProperty('enabled')) {
                    this._fog.setEnabled(fog.enabled);
                }
                if (fog.hasOwnProperty('density')) {
                    this._fog.setDensity(fog.density);
                }
                if (fog.hasOwnProperty('distance')) {
                    this._fog.setDistance(fog.distance);
                }
            }

            if (aChangedSettings.hasOwnProperty('ssao')) {
                let ssao = aChangedSettings.ssao;
                if (ssao.hasOwnProperty('enabled')) {
                    this._ssao.setEnabled(ssao.enabled);
                }
                if (ssao.hasOwnProperty('bias')) {
                    this._ssao.setBias(ssao.bias);
                }
                if (ssao.hasOwnProperty('radius')) {
                    this._ssao.setRadius(ssao.radius);
                }
            }

        }
    }

    protected notifySettings(aChangedSettings: any, aPreviousSettings: any) {
        if (aChangedSettings.hasOwnProperty("background")) {
            let bgModule = aChangedSettings['background'];
            if (bgModule.hasOwnProperty("background")) {
                let bg = bgModule['background'];
                if (bg.hasOwnProperty('type') || bg.hasOwnProperty('color')) {
                    let fogColor = this.sceneAPI.backgroundModule.getFogColor();
                    // set automatic fog color according to background type
                    this._fog.setColor([fogColor[0], fogColor[1], fogColor[2]]);
                    this.recalculate();
                    this.sceneAPI.viewModeModule.scheduleRedraw();
                }
            }
        }

        if (aChangedSettings.hasOwnProperty("geolocation")) {
            let geoLocationModule = aChangedSettings['geolocation'];

            if (geoLocationModule.hasOwnProperty('latitude')
                || geoLocationModule.hasOwnProperty('longitude')
                || geoLocationModule.hasOwnProperty('address')
                || geoLocationModule.hasOwnProperty('addressShort')
                || geoLocationModule.hasOwnProperty('projectionType')
                || geoLocationModule.hasOwnProperty('useLatLng')
            ) {
                // do recompute lighting
                this.recalculate();
            }
        }
    }

    private _updateLightsFromSetting(aLightingSettings: LightingSettings) {
        this.enableHeadLight(this.currentLighting.getHeadLightEnabled());
        this.enableSunLight(this.currentLighting.getSunLightEnabled());
        this.setHeadLightIntensity(this.currentLighting.getHeadLightIntensity());
        this.setSunLightIntensity(this.currentLighting.getSunLightIntensity());
        this._updateSunPosition(this.getSunDirection());
    }

    /***
     * Example function ho to get the sun orientation and elevation from direction vector. Please, do not remove this function event it is not used now.
     * @param {vec3 } aDirectionVector Sun direction vector
     * @private
     */
    private _getSunOrientationAndElevationFromDirection(aDirectionVector: vec3) {
        let sundir = vec3.fromValues(aDirectionVector[0], aDirectionVector[1], aDirectionVector[2]);
        let radius = Math.sqrt(sundir[0] * sundir[0] + sundir[2] * sundir[2]);

        // orientation
        let orientation = Math.acos((sundir[0]) / radius);
        let gamma = Math.asin((sundir[2]) / radius); // we need a sign to determine the half plane for the alpha angle
        if (gamma < 0) {
            orientation = 2 * Math.PI - orientation;
        }
        // offset the orientation to equal the control widget
        orientation += (Math.PI / 2);
        orientation = ((orientation / (2 * Math.PI)) - Math.floor(orientation / (2 * Math.PI))) * 2 * Math.PI; // fit to 0-pi interval

        // elevation
        let elevation = Math.atan(sundir[1] / radius);
        Debug.logInfo("orientation [deg]: " + (((orientation * 180 / 3.14))));
        Debug.logInfo("elevation [deg]:   " + (((elevation * 180 / 3.14))));
    }

    private _updateSunPosition(aPosition: vec3): boolean {
        // Inform shadows module about change
        this.sceneAPI.getShadowsModule().invalidateShadowMapAndLight();

        let viewModeModule = this.sceneAPI.viewModeModule;
        if (!viewModeModule) {
            return false;
        }

        let views = viewModeModule.getViews();
        for (let i = 0; i < views.length; i++) {
            let view = views[i];
            let sunLight = view.getSunLight();
            if (sunLight) {
                sunLight.setPosition(vec4.fromValues(aPosition[0], aPosition[1], aPosition[2], 0.0));
            }
            view.getViewControls().getCamera().sunUpdated();
        }

        this.sceneAPI.viewModeModule.scheduleRedraw();

        return true;
    }

    private _disableShadows(aType) {
        let that = this;
        if (aType === EShadowType.SV) {
            if (this._disableSvCB) {
                this._disableSvCB();
            }
            this.sceneAPI.shadowsModule.disableShadowVolumes(function() {
                if (that._shadowsDisabledCB) {
                    that._shadowsDisabledCB();
                }
            });
        }
        else if (aType === EShadowType.SM) {
            this.sceneAPI.shadowsModule.setShadowMapEnabled(false);
            if (this._shadowsDisabledCB) {
                this._shadowsDisabledCB();
            }
        }
    }

    /**
     *
     * @param aDatetime Datetime format
     * @private
     */
    private _recalculate(aDatetime: any) {
        /*
            //QDateTime temp = _dateTime.toUTC();
            int timeZone = _timeZone;               //@todo: get timezone from country in the future

            QDate date = dt.date();
            QTime time = dt.time();
        */

        let dt = aDatetime;
        let latitude = this.sceneAPI.geoLocationModule.getLatitude();
        let longitude = this.sceneAPI.geoLocationModule.getLongitude();

        let toRad = this._TO_RADIANS;
        let toDeg = this._TO_DEGREES;

        /*
            int H = time.hour() - timeZone;
            if (H > 24)
            {
                H -= 24;
                dt.addDays(1); // ?? dt = dt.addDays(1)
            }
            if (H < 0)
            {
                H += 24;
                dt.addDays(-1); // ?? dt = dt.addDays(-1)
            }
        */

        // to UTC correction
        let Y =   dt.getUTCFullYear();
        let Mon = dt.getUTCMonth() + 1; // 0-11
        let Dat = dt.getUTCDate();

        let H =   dt.getUTCHours();
        let Min = dt.getUTCMinutes();
        let Sec = dt.getUTCSeconds();
        //           367 *  y      )-Math.floor((7/4)*(Math.floor((m+9)/12)+y))               +Math.floor(275*m/9)             + d           - 730531.5+(u/24)
        // javascript: ~~FloatValue = (int)FloatValue
        let JD0 = 367 * Y - ~~((7 * (Y + ~~((Mon + 9) / 12))) / 4) + ~~(275 * Mon / 9) + Dat - 730531.5;
        let UT = H + (Min / 60.0) + (Sec / 3600.0);
        let JD = JD0 + UT / 24.0;

        let T = JD / 36525.0;
        let M = 357.5291 + 35999.0503 * T - 0.0001559 * T * T - 0.00000045 * T * T * T;
        while (M > 360) {M -= 360;}

        let Lo = 280.46645 + 36000.76983 * T + 0.0003032 * T * T;
        while (Lo > 360) {Lo -= 360;}
        let DL = (1.9146 - 0.004817 * T - 0.000014 * T * T) * Math.sin(M * toRad) + (0.019993 - 0.000101 * T) * Math.sin(2 * M * toRad) + 0.00029 * Math.sin(3 * M * toRad);
        let L = Lo + DL;

        let eps = 23.43999 - 0.013 * T;
        let delta = Math.asin(Math.sin(L * toRad) * Math.sin(eps * toRad)) * toDeg;
        let RA = Math.atan2(Math.cos(eps * toRad) * Math.sin(L * toRad), Math.cos(L * toRad)) * toDeg;
        while (RA > 360) {RA -= 360;}
        let GMST = 280.46061837 + 360.98564736629 * JD + 0.000387933 * T * T - T * T * T / 38710000.0;
        while (GMST > 360) {GMST -= 360;}
        let LMST = GMST + longitude;
        let LHA = LMST - RA;
        // double eqt = (Lo-RA)*4;   unused
        // let azm = Math.atan2(-Math.sin(LHA * toRad), Math.cos(latitude * toRad) * Math.tan(delta * toRad) - Math.sin(latitude * toRad) * Math.cos(LHA * toRad)) * toDeg;
        // radians
        let azm = Math.atan2(-Math.sin(LHA * toRad), Math.cos(latitude * toRad) * Math.tan(delta * toRad) - Math.sin(latitude * toRad) * Math.cos(LHA * toRad));
        while (azm > 360) {azm -= 360;}
        // let alt = Math.asin(Math.sin(latitude * toRad) * Math.sin(delta * toRad) + Math.cos(latitude * toRad) * Math.cos(delta * toRad) * Math.cos(LHA * toRad)) * toDeg;
        // radians
        let alt = Math.asin(Math.sin(latitude * toRad) * Math.sin(delta * toRad) + Math.cos(latitude * toRad) * Math.cos(delta * toRad) * Math.cos(LHA * toRad));

        // set results = azimuth (sun orientation), altitude (sun elevation)
        let azmR = Math.round(azm);
        let altR = Math.round(alt);

        // this.setSunOrientation(azmR);
        // this.setSunElevation(altR);

        // this.NORTH_Y_TRANSFORM = -90; // North to Y transform
        // var angleRad = ((azm + this.NORTH_Y_TRANSFORM) * Math.PI) / 180.0;

        // let NORTH_Y_TRANSFORM = - Math.PI * 0.5; // radians

        this.set({
            sunlight: {
                sunOrientationAngle: azm + this._NORTH_Y_TRANSFORM,
                sunElevationAngle: alt
            }
        }, true);

    }

}

Scene.registerSceneAPIModule(LightingModule);
